import type { CSSProperties } from "react"

export const jobActivityPanelAnchorClassName =
  "fixed bottom-20 right-4 z-50 w-[22rem] max-w-[calc(100vw-2rem)] motion-reduce:bottom-4"

/** Fill the sidebar column so active and terminal cards share a width. */
export const panelInlineWidthClassName = "w-full"

/** Overlay chrome — muted surface so the panel lifts off the white canvas. */
export const panelChromeClassName =
  "rounded-md border border-input shadow-sm"

export const panelSurfaceClassName =
  `${panelChromeClassName} bg-muted`

/**
 * Card-stack wash: frost behind the rail, fading into the page on the left
 * and below. Must not sit under `overflow` or `isolate` — those become
 * backdrop roots and `backdrop-blur` samples an empty box. Do not put CSS
 * `filter` on this same node; it also kills the blur.
 */
export const panelAuraClassName =
  "panel-stack-aura pointer-events-none absolute inset-0 bg-background/70 backdrop-blur-md"

export const PANEL_EASE = "ease-[cubic-bezier(0.25,1,0.5,1)]"

/**
 * Opening-wave stagger. Keep the `50ms` step in `panelStaggerDelayClassName` in
 * lockstep — Tailwind only emits the class when the full literal is in source.
 */
export const PANEL_STAGGER_STEP_MS = 50
export const PANEL_STAGGER_MAX_INDEX = 8

/** Holds the enter keyframe during delay; reduced-motion skips the wait. */
export const panelStaggerDelayClassName =
  "motion-safe:fill-mode-both motion-safe:[animation-delay:calc(var(--panel-stagger-index,0)*50ms)] motion-reduce:[animation-delay:0ms]"

export const panelEnterClassName =
  `motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-right-2 motion-safe:duration-150 ${PANEL_EASE} ${panelStaggerDelayClassName} motion-reduce:animate-none`

/**
 * Opening-wave height. Rest at max-content without overflow clipping;
 * `panel-stack-slot.css` animates 0fr → 1fr during stagger only.
 *
 * `min-w-0` on both levels is load-bearing: a card's `truncate` title is
 * `nowrap`, so its min-content width can exceed the column. Without it the
 * grid/flex automatic minimum size floors the slot at that width and the card
 * bleeds past the column, where an enclosing clip box slices its right edge.
 */
export const panelStackSlotClassName = "panel-stack-slot grid min-w-0 grid-rows-[max-content]"

export const panelStackSlotInnerClassName = "min-h-0 min-w-0"

export const panelExitClassName =
  `pointer-events-none motion-safe:animate-out motion-safe:fade-out motion-safe:slide-out-to-right-2 motion-safe:zoom-out-95 motion-safe:duration-150 ${PANEL_EASE} motion-safe:fill-mode-forwards motion-reduce:animate-none`

/** Closes the dismissed card's gap once it has left; see `panel-stack-slot.css`. */
export const panelStackSlotExitClassName = "panel-stack-slot-exit grid min-w-0 grid-rows-[max-content]"

/** Icon control: 44px for coarse pointers, compact on fine hover pointers. */
export const panelIconActionClassName = [
  "inline-flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-sm",
  "[@media(hover:hover)_and_(pointer:fine)]:size-7",
  "text-muted-foreground/70 transition-[background-color,color,transform] duration-150",
  PANEL_EASE,
  "hover:bg-foreground/10 hover:text-foreground",
  "motion-safe:active:scale-90 motion-safe:active:duration-100",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
].join(" ")

/**
 * Hover curve. Symmetric ease-in-out rather than the entrance `PANEL_EASE`,
 * whose front-loaded quart makes a hover read as a snap even when it is slow.
 */
export const PANEL_HOVER_EASE = "ease-[cubic-bezier(0.65,0,0.35,1)]"

/** Slow enough to glide while the pointer crosses a stack of cards. */
export const PANEL_HOVER_MS = 500

/**
 * Card hover. Background only — dock proximity owns the horizontal motion,
 * and a CSS scale would fight it and clip against the scroll column.
 * Keep the literal `duration-500` in sync with `PANEL_HOVER_MS`; Tailwind only
 * emits the class when the full literal appears in source.
 */
export const panelHoverClassName = [
  "group/status origin-center",
  "transition-[background-color] duration-500",
  PANEL_HOVER_EASE,
  "hover:bg-background hover:z-[1]",
].join(" ")

/** Dock proximity shift — driven by `--panel-dock-x` from `usePanelDockShift`. */
export const panelDockTranslateClassName = "[translate:var(--panel-dock-x,0px)_0]"

/** Clickable terminal card — pointer plus the shared hover treatment. */
export const panelInteractiveClassName = `cursor-pointer ${panelHoverClassName}`

/** Matches `duration-150` for JS exit delays. */
export const PANEL_MOTION_MS = 150

/** Height settle that runs after the card has left. Keep in lockstep with `panel-stack-slot.css`. */
export const PANEL_COLLAPSE_MS = 160

/** Card exit plus the settle — how long a dismissed card stays mounted. */
export const PANEL_DISMISS_MS = PANEL_MOTION_MS + PANEL_COLLAPSE_MS

export function panelStaggerStyle(index: number): CSSProperties {
  const clamped = Math.min(Math.max(0, index), PANEL_STAGGER_MAX_INDEX)
  return { "--panel-stagger-index": clamped } as CSSProperties
}
